import { isFunction } from "../is"

export const uniqueBy = <T, KV = string>(arr: T[], key: string | ((item: T) => KV)): T[] => {
  const nextArr: T[] = []

  try {
    const getId = (item: T, k: string | ((item: T) => KV)): any => {
      return typeof k === 'string' ? (item as Record<string, any>)[k] : k(item)
    }
    for (const item of arr) {
      const id = getId(item, key)
      const count = nextArr.filter((it) => getId(it, key) === id).length
      if (count > 0) continue
      nextArr.push(item)
    }
  } catch (e) {
    console.error('uniqueBy() failed.')
    console.error(e)
  }

  return nextArr
}

export const unique = <T>(arr: T[]): T[] => [...Array.from(new Set(arr))] as T[]

export const popped = <T = any>(arr: T[]) => {
  const copy = [...arr]
  copy.pop()
  return copy
}

export const pushed = <T = any>(arr: T[], value: T) => {
  const copy = [...arr]
  copy.push(value)
  return copy
}

export const range = (n: number): number[] => Array.from(Array(n).keys())
export const padLeft = <T = any>(arr: T[], n: number, fillWith: T): T[] =>
  n > 0 ? [...range(n).map(() => fillWith), ...arr] : [...arr]
export const padRight = <T = any>(arr: T[], n: number, fillWith: T): T[] =>
  n > 0 ? [...arr, ...range(n).map(() => fillWith)] : [...arr]
export const rangeTo = (start: number, end: number): number[] => {
  const len = Math.abs(end - start)
  return range(len).map((i) => start + i)
}

export const arrayFit = <T = any>(arr: T[], len: number, padWith: T) => {
  if (arr.length === len) return arr
  if (arr.length > len) return arr.splice(0, len)
  const left = len - arr.length
  if (left <= 0) return arr
  return [...arr, ...range(left).map(() => padWith)]
}

export const linspace = (n: number, step: number = 1): number[] => {
  const arr: number[] = []
  if (step <= 0) return arr

  for (let i = 0; i < n; i += step) {
    arr.push(i)
  }

  return arr
}

export const repeat = <T = number>(arr: T[], n: number): T[] => {
  let rep = [...arr]
  const rn = Math.round(n)
  if (rn <= 0) return rep

  for (let i = 1; i < rn; i++) {
    rep = [...rep, ...arr]
  }

  return rep
}

export const sum = (arr: number[]): number => arr.reduce((a, b) => a + b, 0)

export const reversed = <T>(arr: T[]): T[] => [...arr].reverse()

export const limitArrayLength = <T>(arr: T[], limit: number): T[] => {
  if (arr.length <= limit) return arr
  return [...arr].splice(0, limit)
}

export const toggleItem = <T = any>(arr: T[], item: T): T[] => {
  if (arr.includes(item)) return arr.filter((it) => it !== item)
  return [...arr, item]
}

export const patchItem = <T = any>(
  arr: T[],
  filter: (it: T) => boolean,
  update: (it: T) => T
): T[] => {
  return arr.map((it) => (filter(it) ? update(it) : it))
}

export const upsertItem = <T = any>(
  arr: T[],
  filter: (it: T) => boolean,
  update: (it?: T) => T
): T[] => {
  if (!arr.find((it) => filter(it))) {
    return [...arr, update()]
  }
  return arr.map((it) => (filter(it) ? update(it) : it))
}

export const join = <T>(array: T[], separator: T | ((i: number) => T), skip: number[] = []): T[] => {
  if (array.length === 0) {
    return []
  }

  const result: T[] = []
  for (let i = 0; i < array.length; i++) {
    result.push(array[i])
    if (i < array.length - 1 && !skip.includes(i)) {
      if (isFunction(separator)) {
        result.push(separator(i));
      } else {
        result.push(separator);
      }
    }
  }

  return result
}

export const groupBy = <T = any>(arr: T[], by: (it: T) => string): Record<string, T[]> => {
  return arr.reduce((prev, cur) => {
    const key = by(cur);
    return {
      ...prev,
      [key]: [...(prev[key] || []), cur]
    }
  }, {} as Record<string, T[]>)
}

export const findWithIndex = <T = any>(arr: T[], find: (it: T) => boolean): { item: T, index: number } | undefined => {
  for (let i = 0; i < arr.length; i++) {
    const it = arr[i];
    if (find(it)) {
      return {
        item: it,
        index: i
      }
    }
  }

  return undefined;
}

export const zip = <S1, S2>(
  firstCollection: Array<S1>,
  lastCollection: Array<S2>,
): Array<[S1, S2]> => {
  const length = Math.min(firstCollection.length, lastCollection.length);
  const zipped: Array<[S1, S2]> = [];

  for (let index = 0; index < length; index++) {
    zipped.push([firstCollection[index], lastCollection[index]]);
  }

  return zipped;
};


export const arrPush = <T = any>(arr: Array<T> | undefined | null, ...items: T[]): Array<T> => {
  if (Array.isArray(arr)) {
    arr.push(...items);
    return arr || [];
  }

  return items;
}
